import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Trophy, Gift, Flame, BarChart3, Zap,
  TrendingUp, Star, Target, Heart, Sparkles, ArrowRight,
  Sword, Circle, Eye, Music, Sun, Receipt, Users, Hash,
  Smartphone, Brain, Bell,
} from 'lucide-react';

interface MockupCard {
  path: string;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
  stat: string;
  statLabel: string;
  section: 'core' | 'cross-domain' | 'crypto' | 'survival' | 'strava' | 'reports';
}

const mockups: MockupCard[] = [
  // ─── Core Concepts ───
  {
    path: '/restaurant/mockups/my-earnings',
    title: 'My Earnings',
    subtitle: 'See exactly how much extra you\'re making',
    icon: DollarSign,
    color: 'emerald',
    description: 'Track your upsell bonuses, tip lift, and total earnings impact in real time. Know your worth.',
    stat: '+$847',
    statLabel: 'this month',
    section: 'core',
  },
  {
    path: '/restaurant/mockups/leaderboard',
    title: 'The Arena',
    subtitle: 'Friendly competition, real rewards',
    icon: Trophy,
    color: 'amber',
    description: 'Peloton-style leaderboard. See where you rank, chase your personal best, and celebrate your crew.',
    stat: 'Top 12%',
    statLabel: 'your percentile',
    section: 'core',
  },
  {
    path: '/restaurant/mockups/rewards',
    title: 'Rewards Store',
    subtitle: 'Turn great service into real rewards',
    icon: Gift,
    color: 'purple',
    description: 'Cash back, gift cards, bonus pool payouts. Your upsells earn you points that convert to real money.',
    stat: '2,340 pts',
    statLabel: 'available',
    section: 'core',
  },
  {
    path: '/restaurant/mockups/shift-replay',
    title: 'Shift Replay',
    subtitle: 'Your shift, gamified',
    icon: Flame,
    color: 'orange',
    description: 'Relive your shift like a highlight reel. See streaks, achievements unlocked, and moments that made the difference.',
    stat: '7 streak',
    statLabel: 'best today',
    section: 'core',
  },
  {
    path: '/restaurant/mockups/my-stats',
    title: 'My Stats',
    subtitle: 'Your personal performance dashboard',
    icon: BarChart3,
    color: 'blue',
    description: 'Like Strava for servers. Track your growth over time, set personal goals, and watch yourself level up.',
    stat: 'Level 14',
    statLabel: 'Silver II',
    section: 'core',
  },
  {
    path: '/restaurant/mockups/team-pulse',
    title: 'Team Pulse',
    subtitle: 'Celebrate wins together',
    icon: Heart,
    color: 'pink',
    description: 'See the team\'s live energy. Shout out great moments. Build a culture where everyone wins.',
    stat: '94%',
    statLabel: 'team vibe',
    section: 'core',
  },
  // ─── Cross-Domain Inspiration ───
  {
    path: '/restaurant/mockups/wrapped',
    title: 'Monthly Wrapped',
    subtitle: 'Your Spotify Wrapped, but for serving',
    icon: Music,
    color: 'violet',
    description: 'Swipeable, gorgeous monthly recap. Your earnings, your growth, your "upsell personality," your superlatives.',
    stat: 'Feb 2026',
    statLabel: 'wrapped',
    section: 'cross-domain',
  },
  {
    path: '/restaurant/mockups/character',
    title: 'Character Sheet',
    subtitle: 'Full RPG treatment for your server skills',
    icon: Sword,
    color: 'indigo',
    description: 'Skill trees, stat hexagons, equipped "phrases" with rarity levels. Level up Charisma, Hustle, and Menu Mastery.',
    stat: 'Lvl 14',
    statLabel: 'journeyman',
    section: 'cross-domain',
  },
  {
    path: '/restaurant/mockups/rings',
    title: 'Close Your Rings',
    subtitle: 'Apple Watch rings for your shift',
    icon: Circle,
    color: 'red',
    description: 'Three daily rings: Greetings, Upsells, Drinks. Close all three for bonus points. Build streaks. Get addicted.',
    stat: '79%',
    statLabel: 'closed today',
    section: 'cross-domain',
  },
  // ─── Crypto Bro Energy ───
  {
    path: '/restaurant/mockups/exchange',
    title: 'Upsell Exchange',
    subtitle: 'Your earnings as a live trading floor',
    icon: TrendingUp,
    color: 'green',
    description: 'Ticker tape, candlestick charts, portfolio P&L, and alpha alerts. $WAGYU is pumping. WAGMI.',
    stat: '+49.2%',
    statLabel: '$DUBAI',
    section: 'crypto',
  },
  {
    path: '/restaurant/mockups/bet',
    title: 'Bet On Yourself',
    subtitle: 'Stake points on your own performance',
    icon: Target,
    color: 'cyan',
    description: 'Prediction market meets daily challenges. Pick a target, stake your points, hit the multiplier. NFA.',
    stat: '3.0x',
    statLabel: 'best odds',
    section: 'crypto',
  },
  {
    path: '/restaurant/mockups/whales',
    title: 'Whale Watch',
    subtitle: 'Track the top earners. Copy their playbook.',
    icon: Eye,
    color: 'slate',
    description: 'Follow the highest-earning servers. See their moves. Save their plays. Copy-trade the best. Share alpha.',
    stat: '$4,230',
    statLabel: 'top whale',
    section: 'crypto',
  },
  // ─── When You're Counting Every Dollar ───
  {
    path: '/restaurant/mockups/tonights-plan',
    title: 'Tonight\'s Plan',
    subtitle: 'Exactly what to say, exactly what it\'s worth',
    icon: Sun,
    color: 'amber',
    description: 'Pre-shift cheat sheet. The highest-margin items, word-for-word scripts, and the tip math that makes it real. No guessing.',
    stat: '+$45',
    statLabel: 'extra tonight',
    section: 'survival',
  },
  {
    path: '/restaurant/mockups/live-register',
    title: 'Live Register',
    subtitle: 'Your cash register, updated live',
    icon: Receipt,
    color: 'emerald',
    description: 'Not points. Not scores. Dollars. Watch your earnings tick up in real time. Every upsell shows you exactly what it added to your pocket.',
    stat: '$164.90',
    statLabel: 'so far tonight',
    section: 'survival',
  },
  {
    path: '/restaurant/mockups/people-who-get-it',
    title: 'People Who Get It',
    subtitle: 'They were broke too. Then something changed.',
    icon: Users,
    color: 'slate',
    description: 'Not top performers — real people who were eating staff meals and couch-surfing. Their stories. Their turning points. Their advice.',
    stat: '4 stories',
    statLabel: 'like yours',
    section: 'survival',
  },
  {
    path: '/restaurant/mockups/small-wins',
    title: 'Small Wins',
    subtitle: 'Every upsell buys something real',
    icon: Heart,
    color: 'red',
    description: 'A $6.80 upsell doesn\'t sound like much — until you see it fill a progress bar toward groceries, bus fare, or rent.',
    stat: '$50.40',
    statLabel: 'groceries: done',
    section: 'survival',
  },
  {
    path: '/restaurant/mockups/set-your-number',
    title: 'Set Your Number',
    subtitle: 'Pick a target. See the plan. Go get it.',
    icon: Hash,
    color: 'blue',
    description: 'Not about rankings. What does it take to earn $120 tonight? $160? $200? Break it down table by table until it feels possible.',
    stat: '$160',
    statLabel: 'tonight\'s goal',
    section: 'survival',
  },
  // ─── Strava for Servers ───
  {
    path: '/restaurant/mockups/onboarding',
    title: 'Server Onboarding',
    subtitle: 'The trojan horse — free app, then ask your manager',
    icon: Smartphone,
    color: 'emerald',
    description: 'BYOD app servers download on their own phone. Free tier hooks them. Locked features make them ask management to connect the POS.',
    stat: '87%',
    statLabel: 'managers say yes',
    section: 'strava',
  },
  {
    path: '/restaurant/mockups/shift-live',
    title: 'Live Shift Dashboard',
    subtitle: 'Everything you need, mid-shift, at a glance',
    icon: Zap,
    color: 'orange',
    description: 'Real-time earnings, active bounties, live leaderboard, and AI cheat codes — all on one screen. Push notifications for flash bounties.',
    stat: '$142.60',
    statLabel: 'tonight so far',
    section: 'strava',
  },
  {
    path: '/restaurant/mockups/flash-bounties',
    title: 'Flash Bounties',
    subtitle: 'Cash bounties and revenue shares, live',
    icon: Zap,
    color: 'amber',
    description: 'Kitchen sets bounties. Servers race to claim them. Revenue sharing on premium items. Real cash on top of tips.',
    stat: '$23',
    statLabel: 'earned this week',
    section: 'strava',
  },
  {
    path: '/restaurant/mockups/cheat-codes',
    title: 'AI Cheat Codes',
    subtitle: 'Context-aware scripts that actually convert',
    icon: Brain,
    color: 'indigo',
    description: 'Word-for-word scripts based on time of day, weather, table size, and what\'s selling. Copy, adapt, close. Your unfair advantage.',
    stat: '82%',
    statLabel: 'success rate',
    section: 'strava',
  },
  // ─── Reports ───
  {
    path: '/restaurant/mockups/gen-bbq-analysis',
    title: 'Gen BBQ Deep Analysis',
    subtitle: 'Server performance breakdown for Gen Korean BBQ',
    icon: BarChart3,
    color: 'cyan',
    description: 'Comprehensive analysis of server performance data — upsell rates, revenue impact, shift patterns, and actionable insights.',
    stat: 'Report',
    statLabel: 'deep dive',
    section: 'reports',
  },
  {
    path: '/restaurant/mockups/jess-coaching-plan',
    title: 'Project Jess',
    subtitle: '90-day star server transformation plan',
    icon: Star,
    color: 'purple',
    description: 'A personalized 90-day coaching plan to transform a server into a top performer — week-by-week goals, scripts, and milestones.',
    stat: '90 days',
    statLabel: 'transformation',
    section: 'reports',
  },
  {
    path: '/restaurant/mockups/roi-calculator',
    title: 'ROI Calculator',
    subtitle: 'Interactive ROI model for Bespoke',
    icon: DollarSign,
    color: 'emerald',
    description: 'Calculate the revenue impact of Bespoke for any restaurant — plug in your numbers and see the projected return.',
    stat: 'ROI',
    statLabel: 'calculator',
    section: 'reports',
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; iconBg: string; statText: string }> = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', iconBg: 'bg-emerald-100', statText: 'text-emerald-600' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', iconBg: 'bg-amber-100', statText: 'text-amber-600' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', iconBg: 'bg-purple-100', statText: 'text-purple-600' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', iconBg: 'bg-orange-100', statText: 'text-orange-600' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', iconBg: 'bg-blue-100', statText: 'text-blue-600' },
  pink: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', iconBg: 'bg-pink-100', statText: 'text-pink-600' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', iconBg: 'bg-violet-100', statText: 'text-violet-600' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', iconBg: 'bg-indigo-100', statText: 'text-indigo-600' },
  red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', iconBg: 'bg-red-100', statText: 'text-red-600' },
  green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', iconBg: 'bg-green-100', statText: 'text-green-600' },
  cyan: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', iconBg: 'bg-cyan-100', statText: 'text-cyan-600' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', iconBg: 'bg-slate-200', statText: 'text-slate-600' },
};

const sections = [
  { key: 'core', label: 'Core Concepts', subtitle: 'The essential pitch — show servers the money' },
  { key: 'cross-domain', label: 'Stolen from the Best', subtitle: 'Spotify Wrapped + RPGs + Apple Watch' },
  { key: 'crypto', label: 'Full Degen Mode', subtitle: 'Robinhood + Polymarket + Crypto Twitter. WAGMI.' },
  { key: 'strava', label: 'Strava for Servers', subtitle: 'BYOD app. Flash bounties. Revenue sharing. The trojan horse that makes servers demand the tool.' },
  { key: 'survival', label: 'When You\'re Counting Every Dollar', subtitle: 'For the server who needs this shift to matter. Practical. Honest. No games.' },
  { key: 'reports', label: 'Analysis Reports', subtitle: 'Deep dives into real restaurant data and server performance.' },
];

const ServerHub: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-gray-900 to-gray-950" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.2) 0%, transparent 50%)',
        }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-medium mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            CONCEPT MOCKUPS
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
            What if your app made servers <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">richer</span>?
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-3">
            These mockups explore the idea: don't build a tool that watches servers.
            Build a tool servers <em>demand</em> because it helps them earn more money.
          </p>
          <p className="text-sm text-gray-500 max-w-xl mx-auto mb-8">
            "If the staff demands the tool because it helps them make more money, the managers and owners will gladly adopt it."
          </p>

          {/* Core thesis cards — inside the hero */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto text-left">
            {[
              { icon: DollarSign, label: 'Show me the money', desc: 'Servers care about tips. Connect upsells directly to their earnings.' },
              { icon: Zap, label: 'Not Big Brother', desc: 'A coach, not a camera. Help them get better, not catch them slipping.' },
              { icon: TrendingUp, label: 'Bottom-up demand', desc: 'Make servers ask management for this tool, not the other way around.' },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <item.icon className="w-4 h-4 text-indigo-400" />
                  <h3 className="text-sm font-semibold text-white">{item.label}</h3>
                </div>
                <p className="text-xs text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mockup sections */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-12">
        {sections.map((section) => {
          const sectionMockups = mockups.filter(m => m.section === section.key);
          return (
            <div key={section.key}>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-white">{section.label}</h2>
                <p className="text-sm text-gray-600">{section.subtitle}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {sectionMockups.map((m) => {
                  const c = colorMap[m.color] || colorMap.blue;
                  return (
                    <button
                      key={m.path}
                      onClick={() => navigate(m.path)}
                      className="text-left bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-all duration-200 hover:shadow-lg hover:shadow-black/20 group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-10 h-10 rounded-lg ${c.iconBg} flex items-center justify-center`}>
                          <m.icon className={`w-5 h-5 ${c.text}`} />
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${c.statText}`}>{m.stat}</div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wider">{m.statLabel}</div>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-0.5">{m.title}</h3>
                      <p className="text-xs text-gray-400 mb-3">{m.subtitle}</p>
                      <p className="text-xs text-gray-600 mb-4">{m.description}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-600 group-hover:text-indigo-400 transition-colors">
                        Explore <ArrowRight className="w-3 h-3" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServerHub;
